import React, { useEffect, useState } from "react";
import { useNavigate, Outlet, useLocation } from "react-router";
import styled from "@emotion/styled";
import { BrandLogo } from "components/Logo";
import { BRAND_LOGO_VARIANTS } from "constants/brands";
import { breakpoints, colors, gradients } from "style";
import Icon, { ICON_TYPE, ICON_SIZE } from "components/Icon";
import { Link } from "components/Links";
import { useAuth, useLinks, useLogout, usePlatformInfo } from "hooks";
import { ShowcaseVideo } from "components/Tutorials";
import { ShowcaseInviteButton } from "components/Showcase";
import { MenuItemAccount, MenuItemFaq, MenuItemsByUserType } from "./MenuItems";
import SideNavLink from "components/Nav/SideNav/SideNavLink";
import SideNavTooltip from "components/Nav/SideNav/SideNavTooltip";
import SideNavExpander from "components/Nav/SideNav/SideNavExpander";
import Impersonating from "components/Impersonating";
import { ADMIN_MENU } from "components/Nav/SideNav/MenuItems/MenuItemsByUserType";
import Tooltip from "components/Tooltip";
import { OmnisearchModal } from "components/Omnisearch";
import { FormattedMessage, useIntl } from "react-intl";

/**
 * Side navigation
 */
const SideNav = (props) => {
  const intl = useIntl();
  const { isShowcase, isAdmin } = useAuth();
  const { isMac } = usePlatformInfo();
  const links = useLinks();
  const [showMenu, setShowMenu] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const { pathname } = useLocation();
  const { handleLogout, loading } = useLogout();
  const navigate = useNavigate();

  /**
   * @description Handle keyboard shortcut for opening search modal
   */
  useEffect(() => {
    if (!isAdmin) return;

    const handleKeyDown = (e) => {
      const isCtrlOrCmd = isMac ? e.metaKey : e.ctrlKey;

      if (isCtrlOrCmd && e.key.toLowerCase() === "k") {
        e.preventDefault(); // prevent browser search
        setShowSearch(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isAdmin]);

  /**
   * @description Close the menu on route change
   */
  useEffect(() => {
    setShowMenu(false);
  }, [pathname]);

  /**
   * @description Compact view for admins
   */
  if (isAdmin) {
    return (
      <>
        <Nav isMinimized {...props}>
          <div>
            <LogoContainer isMinimized>
              <Logo variant={BRAND_LOGO_VARIANTS.icon} />
            </LogoContainer>
          </div>
          <IconMenu>
            <Tooltip tooltipId={`Nav.SideNav.LabelSearch`} hideIcon>
              <StyledLink onClick={() => setShowSearch(true)}>
                <IconContainer className="icon-container">
                  <Icon type={ICON_TYPE.search} size={ICON_SIZE.xlarge} color={colors.white} />
                </IconContainer>
                <ShortcutHint>
                  <FormattedMessage
                    id="Nav.SideNav.LabelSearch.ShortcutHint"
                    values={{ baseKey: isMac ? "⌘" : "Ctrl" }}
                  />
                </ShortcutHint>
              </StyledLink>
            </Tooltip>
            {ADMIN_MENU(links).map((item, index) => (
              <Tooltip tooltipId={item.textId} key={index} hideIcon>
                <StyledLink routerEnabled to={item.to}>
                  <IconContainer className="icon-container">
                    <Icon key={index} type={item.icon} size={ICON_SIZE.xlarge} color={colors.white} />
                  </IconContainer>
                </StyledLink>
              </Tooltip>
            ))}
            <Tooltip tooltipId={`Global.Logout`} hideIcon>
              <StyledLink disabled={loading} onClick={() => handleLogout(() => navigate(links.login))}>
                <IconContainer className="icon-container">
                  <Icon type={ICON_TYPE.logout} size={ICON_SIZE.xlarge} color={colors.white} />
                </IconContainer>
              </StyledLink>
            </Tooltip>
          </IconMenu>
          <AdminAccountSection>
            <Tooltip tooltipId="Nav.SideNav.LabelPersonalSettings" hideIcon>
              <StyledLink
                routerEnabled
                to={links.user_settings}
                aria-label={intl.formatMessage({ id: "Nav.SideNav.LabelPersonalSettings" })}
              >
                <IconContainer className="icon-container">
                  <PersonalSettingsIcon aria-hidden="true">
                    <PersonalSettingsUser type={ICON_TYPE.user} size={ICON_SIZE.large} color={colors.white} />
                    <PersonalSettingsGear type={ICON_TYPE.gear} size={ICON_SIZE.large} color={colors.white} />
                  </PersonalSettingsIcon>
                </IconContainer>
              </StyledLink>
            </Tooltip>
            <MenuItemAccount hideName />
          </AdminAccountSection>
        </Nav>
        <ChildrenContainer isMinimized>
          <Outlet />
        </ChildrenContainer>
        <Impersonating />
        {showSearch && <OmnisearchModal show={showSearch} onClose={() => setShowSearch(false)} />}
      </>
    );
  }

  /**
   * @description Default view
   */
  return (
    <>
      <Nav style={{ "--mobile-nav-display": showMenu ? "flex" : "none" }} {...props}>
        <div>
          <LogoContainer>
            <Logo variant={BRAND_LOGO_VARIANTS.icon} />
          </LogoContainer>
        </div>
        <MenuItemsByUserType />
        <Spacer />
        <StyledShowcaseButton isShowcase={isShowcase} />
        <ShowcaseVideo isShowcase={isShowcase} />
        <MenuItemFaq />
        <MenuItemAccount />
        <MobileCloseMenu onClick={() => setShowMenu(false)}>
          <Icon type={ICON_TYPE.close} size={ICON_SIZE.xlarge} color={"inherit"} />
        </MobileCloseMenu>
      </Nav>
      <MobileNav>
        <MobileMenu>
          <Icon
            type={ICON_TYPE.listLines}
            size={ICON_SIZE.xlarge}
            color={"inherit"}
            onClick={() => setShowMenu(!showMenu)}
          />
        </MobileMenu>
        <div>
          <Logo variant={BRAND_LOGO_VARIANTS.icon} />
        </div>
      </MobileNav>
      <ChildrenContainer>
        <Outlet />
      </ChildrenContainer>
      <Impersonating />
    </>
  );
};

const AdminAccountSection = styled.div`
  margin: auto auto 0 auto;
  padding-top: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  flex-shrink: 0;
`;

const IconMenu = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  align-items: center;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  margin: 0 -2rem;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  > * {
    flex-shrink: 0;
  }
`;

const IconContainer = styled.div`
  --icon-badge-background: rgb(38, 38, 76);
  width: 3rem;
  height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  transition: all 0.3s ease;
  border-radius: 0.6rem;
  transform: scale(1);
`;

const PersonalSettingsIcon = styled.span`
  position: relative;
  display: inline-flex;
  width: ${ICON_SIZE.xlarge};
  height: ${ICON_SIZE.xlarge};
`;

const PersonalSettingsUser = styled(Icon)`
  position: absolute;
  top: -0.1rem;
  left: -0.3rem;
  display: inline-block;
`;

const PersonalSettingsGear = styled(Icon)`
  position: absolute;
  top: auto;
  right: -0.3rem;
  bottom: -0.3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.8rem;
  height: 1.8rem;
  border-radius: 50%;
  background: var(--icon-badge-background);
`;

const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 0.5rem;
  cursor: pointer;

  &:hover .icon-container,
  &.active .icon-container {
    --icon-badge-background: rgba(87, 87, 179, 1);
    background: rgba(87, 87, 179, 1);
    transform: scale(1.1);
  }

  &[disabled] {
    opacity: 0.2;
    cursor-events: none;

    &:hover {
      background: none;
    }
  }
`;

const ShortcutHint = styled.span`
  font-size: 1rem;
  color: ${colors.white};
  background: rgba(255, 255, 255, 0.1);
  padding: 0.1rem 0.4rem;
  border-radius: 0.4rem;
  font-family: monospace;
  line-height: 1.3;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 6rem;
`;

const NAV_WIDTH = `25rem`;

const Spacer = styled.div`
  flex-grow: 1;
`;

const StyledShowcaseButton = styled(ShowcaseInviteButton)`
  margin-bottom: 2rem;
`;

const LogoContainer = styled.div`
  flex-shrink: 0;
  margin-bottom: ${({ isMinimized }) => (isMinimized ? `2rem` : `2rem`)};
  margin-top: ${({ isMinimized }) => (isMinimized ? `0rem` : `1rem`)};
  width: ${({ isMinimized }) => (isMinimized ? `4rem` : `5rem`)};
  height: ${({ isMinimized }) => (isMinimized ? `4rem` : `5rem`)};
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
`;

const Logo = styled(BrandLogo)`
  width: 3rem;
`;

const Nav = styled.nav`
  width: ${({ isMinimized }) => (isMinimized ? `8rem` : NAV_WIDTH)};
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  background: ${gradients.sideBarGradient};
  padding: ${({ isMinimized }) => (isMinimized ? `2rem` : `3rem`)};
  color: ${colors.white};
  display: flex;
  flex-direction: column;
  gap: 0rem;
  overflow-y: ${({ isMinimized }) => (isMinimized ? "hidden" : "auto")};
  z-index: 11;

  ${({ isMinimized }) =>
    !isMinimized &&
    `
    @media (max-width: ${breakpoints.tablet}) {
      display: var(--mobile-nav-display, "none");
      top: 0;
      left: 0;
      width: 100%;
      max-width: 45rem;
    }
  `}
`;

const MobileNav = styled.nav`
  display: none;
  width: 100%;
  background: ${gradients.sideBarGradient};
  padding: 1rem 3rem;
  height: 5rem;
  position: sticky;
  gap: 1rem;
  color: ${colors.white};
  top: 0;
  z-index: 10;

  @media (max-width: ${breakpoints.tablet}) {
    display: flex;
    align-items: center;
    gap: 2rem;
    justify-content: space-between;
    padding: 0 2rem;
  }
`;

const MobileMenu = styled.nav`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MobileCloseMenu = styled.nav`
  position: absolute;
  top: 2rem;
  right: 2rem;
  display: none;

  @media (max-width: ${breakpoints.tablet}) {
    display: block;
  }
`;

const ChildrenContainer = styled.div`
  margin-left: ${({ isMinimized }) => (isMinimized ? `8rem` : NAV_WIDTH)};

  ${({ isMinimized }) =>
    !isMinimized &&
    `
    @media (max-width: ${breakpoints.tablet}) {
      margin-left: 0;
    }
  `}
`;

export { SideNavLink, SideNavTooltip, SideNavExpander, NAV_WIDTH };

export default SideNav;
